// The inbox v4 kit: TodoCard, DraftCard, DoneRow, SlotPicker, AgentBubble, Greeting, ToggleRow,
// InboxHeader, InboxRow, InboxText, InboxButton, InboxField, InboxFilter, InboxSteps.
// Every size and colour is read from the v4 design board; Flutter twin: inboxkit.dart.
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import agentLogo from '../assets/agent-logo.webp';
import { Icon } from './primitives';
import { resetButton } from './ui';
import { inboxPalette, SERIF_FONT } from './inboxPalette';
import { PageHeaderHostContext } from '../pageHeaderContext';

const STROKE = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.7, strokeLinecap: 'round', strokeLinejoin: 'round' };

const GLYPH_PATHS = {
  calendar: [<rect key="r" x="3.5" y="5" width="17" height="15" rx="3" />, <path key="p" d="M3.5 10h17M8 3v4M16 3v4" />],
  sparkle: [<path key="p" d="M12 3.5 13.8 10.2 20.5 12 13.8 13.8 12 20.5 10.2 13.8 3.5 12 10.2 10.2Z" />],
  clock: [<circle key="c" cx="12" cy="12" r="8.5" />, <path key="p" d="M12 7.5V12l3 2" />],
  mail: [<rect key="r" x="3.5" y="5.5" width="17" height="13" rx="3" />, <path key="p" d="m4.5 7 7.5 6 7.5-6" />],
  lock: [<rect key="r" x="5" y="10.5" width="14" height="10" rx="2.5" />, <path key="p" d="M8 10.5V8a4 4 0 0 1 8 0v2.5" />],
  'arrow-right': [<path key="p" d="M5 12h13M13 6l6 6-6 6" />],
  card: [<rect key="r" x="3" y="6" width="18" height="12.5" rx="2.5" />, <path key="p" d="M3 10h18" />],
  file: [<path key="a" d="M7 3.5h7l4 4V20a.5.5 0 0 1-.5.5h-10.5A.5.5 0 0 1 6.5 20V4a.5.5 0 0 1 .5-.5Z" />, <path key="b" d="M14 3.5V8h4" />],
  doc: [<path key="a" d="M7 3.5h7l4 4V20a.5.5 0 0 1-.5.5h-10.5A.5.5 0 0 1 6.5 20V4a.5.5 0 0 1 .5-.5Z" />, <path key="b" d="M14 3.5V8h4" />],
  arrow: [<path key="p" d="M5 12h13M13 6l6 6-6 6" />],
  check: [<path key="p" d="m5 12.5 4.5 4.5L19 7.5" />],
  undo: [<path key="a" d="M9 14 4 9l5-5" />, <path key="b" d="M4 9h10a6 6 0 0 1 0 12h-2" />],
  alert: [<circle key="c" cx="12" cy="12" r="8.5" />, <path key="p" d="M12 7.5v5M12 16h.01" />],
  'wifi-off': [<path key="a" d="M2 8.5a15 15 0 0 1 20 0M5.5 12a10 10 0 0 1 13 0M9 15.5a5 5 0 0 1 6 0" />, <path key="b" d="M12 19h.01" />, <path key="c" d="m3 3 18 18" />],
  search: [<circle key="c" cx="11" cy="11" r="7" />, <path key="p" d="m20 20-3.5-3.5" />],
  more: [<circle key="a" cx="5" cy="12" r="1.2" />, <circle key="b" cx="12" cy="12" r="1.2" />, <circle key="c" cx="19" cy="12" r="1.2" />],
  plus: [<path key="p" d="M12 5v14M5 12h14" />],
  'send-up': [<path key="p" d="M12 19V5M6 11l6-6 6 6" />],
  mic: [<rect key="r" x="9" y="3" width="6" height="11" rx="3" />, <path key="p" d="M5 11a7 7 0 0 0 14 0M12 18v3" />],
};

/** The design glyph `name` when the inbox kit draws it, else null (Flutter twin: `viewIrInboxGlyph`). */
// eslint-disable-next-line react-refresh/only-export-components -- shared glyph helper for chat.jsx
export function inboxGlyph(name, { size = 16, color, stroke = 1.7 } = {}) {
  const paths = GLYPH_PATHS[name];
  if (!paths) return null;
  return (
    <svg aria-hidden width={size} height={size} viewBox="0 0 24 24" style={{ flex: '0 0 auto', color }} {...STROKE} strokeWidth={stroke}>
      {paths}
    </svg>
  );
}

function Glyph({ name, size = 18, color }) {
  const paths = GLYPH_PATHS[name];
  if (!paths) return <Icon props={{ name, size, tone: color || 'var(--text)' }} />;
  return (
    <svg aria-hidden width={size} height={size} viewBox="0 0 24 24" style={{ flex: '0 0 auto', color }} {...STROKE}>
      {paths}
    </svg>
  );
}

function Check({ size, color }) {
  return (
    <svg aria-hidden width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="m5 12.5 4.5 4.5L19 7.5" />
    </svg>
  );
}

function Chevron({ color }) {
  return (
    <svg aria-hidden width="16" height="16" viewBox="0 0 24 24" style={{ flex: '0 0 auto' }} fill="none" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="m9 6 6 6-6 6" />
    </svg>
  );
}

function UndoGlyph() {
  return (
    <svg aria-hidden width="14" height="14" viewBox="0 0 24 24" {...STROKE}>
      <path d="M9 14 4 9l5-5" />
      <path d="M4 9h10a6 6 0 0 1 0 12h-2" />
    </svg>
  );
}

const text = (v) => (v == null ? '' : String(v));

const oneLine = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 };

// Inter's natural line box; the page's inherited 1.5 would make every inbox row taller than drawn.
const TYPE = 'normal';

const clamp = (lines) => ({ display: '-webkit-box', WebkitLineClamp: lines, WebkitBoxOrient: 'vertical', overflow: 'hidden' });

export function AgentMark({ size, palette }) {
  return (
    <span data-agent-mark="" style={{
      width: size, height: size, borderRadius: '50%', background: '#0A0A0A', boxShadow: palette.markRing,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto',
    }}>
      <img src={agentLogo} alt="" style={{ width: size * 0.78, height: size * 0.78, display: 'block' }} />
    </span>
  );
}

export function Face({ src, initial, size, palette, ring, tileColor, letterColor }) {
  const [failedSrc, setFailedSrc] = useState(null);
  const [loadedSrc, setLoadedSrc] = useState(null);
  const failed = failedSrc === src;
  const ringStyle = ring ? { border: `2px solid ${ring}`, boxSizing: 'content-box' } : null;
  const letter = text(initial).trim().charAt(0).toUpperCase();
  const tileStyle = {
    width: size, height: size, borderRadius: '50%', background: tileColor || palette.tile, color: letterColor || '#FFFFFF',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto',
    fontWeight: 700, fontSize: size * 0.4, lineHeight: 1,
  };
  if (src && !failed) {
    // The letter shows until the photo has loaded, so a slow photo never leaves an empty circle.
    return (
      <span style={{ position: 'relative', display: 'inline-flex', flex: '0 0 auto', borderRadius: '50%', ...ringStyle }}>
        <span aria-hidden style={tileStyle}>{letter}</span>
        <img data-face="photo" src={src} alt="" onLoad={() => setLoadedSrc(src)} onError={() => setFailedSrc(src)} style={{
          position: 'absolute', inset: 0, width: size, height: size, borderRadius: '50%', objectFit: 'cover', display: 'block',
          opacity: loadedSrc === src ? 1 : 0,
        }} />
      </span>
    );
  }
  return <span data-face="tile" aria-hidden style={{ ...tileStyle, ...ringStyle }}>{letter}</span>;
}

function Tag({ label, ink, palette }) {
  return (
    <span data-tag={ink ? 'ink' : 'plain'} style={{
      height: 20, padding: '0 7px', borderRadius: 5, display: 'inline-flex', alignItems: 'center', flex: '0 0 auto',
      background: ink ? palette.ink : palette.tagBg, color: ink ? palette.onInk : palette.tagInk,
      fontSize: 11, fontWeight: 500, whiteSpace: 'nowrap', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis',
    }}>{label}</span>
  );
}

const MAX_CHOICES = 3;

/**
 * `submitStatus` (app 1.1.4) for an inbox control: `spinning({row, rowKey, choice})` is true when the armed
 * status names this row (`rowKey`, default `row_id`) and chip (`choice_id`), or names neither. The 8 s
 * fallback lives in the interpreter.
 */
function submitSpin(slots) {
  const status = slots?.submitStatus?.payload;
  const submitting = Boolean(status?.submitting);
  return ({ row, rowKey = 'row_id', choice } = {}) => {
    if (!submitting) return false;
    const armedRow = status[rowKey];
    if (row != null && armedRow != null && String(armedRow) !== String(row)) return false;
    const armedChoice = status.choice_id;
    if (choice != null && armedChoice != null && String(armedChoice) !== String(choice)) return false;
    return true;
  };
}

function Spinner({ size, color }) {
  return (
    <span data-inbox-spinner="" aria-hidden="true" style={{
      position: 'absolute', left: '50%', top: '50%', width: size, height: size, margin: `${-size / 2}px 0 0 ${-size / 2}px`,
      boxSizing: 'border-box', borderRadius: '50%', border: `${size <= 12 ? 1.6 : 2}px solid ${color}`,
      borderRightColor: 'transparent', animation: 'viNativeSpin 0.8s linear infinite',
    }} />
  );
}

/** Keeps the label's footprint (hidden) and draws a spinner over it, so the control keeps its size. */
function SpinInPlace({ spin, size, color, children }) {
  if (!spin) return children;
  return (
    <span style={{ position: 'relative', display: 'inline-flex', minWidth: 0 }}>
      <span style={{ visibility: 'hidden', display: 'inline-flex', minWidth: 0 }}>{children}</span>
      <Spinner size={size} color={color} />
    </span>
  );
}

function choiceList(raw) {
  return (Array.isArray(raw) ? raw : [])
    .filter((c) => c && typeof c === 'object' && text(c.label))
    .slice(0, MAX_CHOICES);
}

function TodoSkeleton({ p }) {
  const bar = (width, height, extra) => (
    <i style={{ display: 'block', width, maxWidth: '100%', height, borderRadius: height / 2, background: p.tagBg, ...extra }} />
  );
  return (
    <div data-todo-card="skeleton" aria-hidden style={{
      lineHeight: TYPE, border: `1px solid ${p.cardLine}`, borderRadius: 20, padding: 14, background: p.card, boxSizing: 'border-box', width: '100%',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <i style={{ width: 36, height: 36, borderRadius: '50%', background: p.tagBg, display: 'block' }} />
        <div>{bar(96, 12)}{bar(64, 10, { marginTop: 6 })}</div>
      </div>
      {bar(210, 16, { marginTop: 14 })}
      {bar(280, 12, { marginTop: 8 })}
      <div style={{ display: 'flex', gap: 6, marginTop: 12 }}>{bar(96, 32)}{bar(84, 32)}</div>
    </div>
  );
}

const OFFLINE_NOTE = 'Needs a connection. Nothing was sent.';

/** The note an offline tap reveals under a dimmed action; it clears when the view is back online. */
function useOfflineNote(offline) {
  const [noted, setNoted] = useState(false);
  useEffect(() => { if (!offline) setNoted(false); }, [offline]);
  return [offline && noted, () => setNoted(true)];
}

function OfflineNote({ value, color, top = 8 }) {
  return <div data-offline-note="" role="status" style={{ fontSize: 12.5, color, marginTop: top, ...oneLine }}>{text(value) || OFFLINE_NOTE}</div>;
}

export function TodoCard({ props = {}, ctx, emit, slots }) {
  const p = inboxPalette(ctx);
  const offline = props.offline === true;
  const [noted, note] = useOfflineNote(offline);
  const spinning = submitSpin(slots);
  const rowId = ctx?.item?.id ?? null;
  if (props.skeleton === true) return <TodoSkeleton p={p} />;
  const choices = choiceList(props.choices);
  const primaryIndex = Math.max(0, choices.findIndex((c) => c.primary === true));
  const off = props.choicesDisabled === true || offline;
  const onTap = emit ? () => emit('tap', {}) : undefined;
  return (
    <div data-todo-card="" role={onTap ? 'button' : undefined} tabIndex={onTap ? 0 : undefined} onClick={onTap} style={{
      lineHeight: TYPE, border: `1px solid ${p.cardLine}`, borderRadius: 20, padding: 14, background: p.card, color: p.ink,
      boxSizing: 'border-box', width: '100%', minWidth: 0, cursor: onTap ? 'pointer' : 'default',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Face src={props.avatar} initial={props.initial || props.name} size={36} palette={p} />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <span style={{ fontSize: 14, fontWeight: 600, ...oneLine }}>{text(props.name)}</span>
          {props.meta ? <span style={{ fontSize: 12, color: p.meta, marginTop: 1, ...oneLine }}>{text(props.meta)}</span> : null}
        </div>
        {props.tag ? <Tag label={text(props.tag)} palette={p} /> : null}
      </div>
      {props.title ? (
        <div data-todo-title="" style={{ fontSize: 16.5, lineHeight: 1.3, fontWeight: 600, letterSpacing: '-0.1px', marginTop: 11, ...clamp(2) }}>
          {text(props.title)}
        </div>
      ) : null}
      {props.note ? (
        <div style={{ fontSize: 13.5, lineHeight: 1.45, color: p.note, marginTop: 3, ...clamp(2) }}>{text(props.note)}</div>
      ) : null}
      {choices.length ? (
        <div style={{ display: 'flex', gap: 6, marginTop: 12, flexWrap: 'wrap' }}>
          {choices.map((c, i) => {
            const solid = i === primaryIndex;
            const spin = spinning({ row: rowId, choice: c.id });
            const held = off || (!spin && choices.some((x) => spinning({ row: rowId, choice: x.id })));
            const label = text(c.label);
            return (
              <button
                key={c.id ?? i}
                type="button"
                data-choice={solid ? 'primary' : 'secondary'}
                data-spinning={spin || undefined}
                disabled={(off && !offline) || spin || (held && !off)}
                aria-disabled={held || undefined}
                aria-busy={spin || undefined}
                onClick={(e) => {
                  e.stopPropagation();
                  if (offline) {
                    note();
                    emit?.('offline', {});
                  } else if (!held && !spin) {
                    emit?.('choice', {
                      id: c.id, choice_id: c.id, label: c.label, kind: c.kind ?? null, primary: solid,
                      ...(rowId != null ? { row_id: rowId } : {}),
                    });
                  }
                }}
                style={{
                  ...resetButton, height: 32, padding: '0 13px', borderRadius: 16, boxSizing: 'border-box',
                  border: `1px solid ${solid ? p.ink : p.chipLine}`, background: solid ? p.ink : 'transparent',
                  color: solid ? p.onInk : p.ink, fontSize: 13, fontWeight: 500, display: 'inline-flex', alignItems: 'center',
                  maxWidth: '100%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  opacity: held && !spin ? 0.38 : 1, cursor: held || spin ? 'default' : 'pointer',
                }}
              >{spin ? <SpinInPlace spin size={12} color={solid ? p.onInk : p.ink}>{label}</SpinInPlace> : label}</button>
            );
          })}
        </div>
      ) : null}
      {noted && choices.length ? <OfflineNote value={props.offlineNote} color={p.meta} /> : null}
    </div>
  );
}

function quoted(v) {
  const s = text(v).trim();
  if (!s) return '';
  return /^["“'‘]/.test(s) ? s : `"${s}"`;
}

export function DraftCard({ props = {}, ctx, emit, slots }) {
  const p = inboxPalette(ctx);
  const d = p.draft;
  const busy = props.busy === true;
  const offline = props.offline === true;
  const rowId = ctx?.item?.id ?? null;
  const spin = !offline && submitSpin(slots)({ row: rowId });
  const [noted, note] = useOfflineNote(offline);
  const button = (solid) => ({
    ...resetButton, flex: 1, height: 38, borderRadius: 19, fontSize: 14, fontWeight: 600,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 0,
    background: solid ? d.solidBg : d.ghostBg, color: solid ? d.solidInk : d.ghostInk,
  });
  return (
    <div data-draft-card="" style={{ lineHeight: TYPE, background: d.bg, color: d.ink, borderRadius: 20, padding: 14, boxSizing: 'border-box', width: '100%', minWidth: 0 }}>
      {props.eyebrow ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, fontWeight: 500, color: d.muted }}>
          <AgentMark size={22} palette={p} />
          <span style={oneLine}>{text(props.eyebrow)}</span>
        </div>
      ) : null}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: props.eyebrow ? 12 : 0 }}>
        {/* The inverted card needs a light monogram in both themes or the tile vanishes into it. */}
        <Face src={props.avatar} initial={props.initial || props.title} size={32} palette={p}
          tileColor={ctx?.appearance === 'dark' ? '#E5E5EA' : '#F2F2F4'} letterColor="#0A0A0A" />
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
          <span style={{ fontSize: 14.5, fontWeight: 600, ...oneLine }}>{text(props.title)}</span>
          {props.preview ? <span style={{ fontSize: 13, color: d.muted, marginTop: 2, ...oneLine }}>{quoted(props.preview)}</span> : null}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <button type="button" data-draft-review="" style={button(false)} onClick={() => emit?.('review', {})}>
          {text(props.reviewLabel) || 'Review'}
        </button>
        <button type="button" data-draft-send="" data-spinning={spin || undefined} disabled={(busy || spin) && !offline}
          aria-busy={busy || spin || undefined}
          aria-disabled={offline || undefined}
          style={{ ...button(true), opacity: offline ? 0.38 : busy ? 0.6 : 1, cursor: busy || offline || spin ? 'default' : 'pointer' }}
          onClick={() => {
            if (offline) {
              note();
              emit?.('offline', {});
            } else if (!busy && !spin) emit?.('send', rowId != null ? { row_id: rowId } : {});
          }}>
          {spin
            ? <SpinInPlace spin size={16} color={d.solidInk}>{text(props.sendLabel) || 'Send'}</SpinInPlace>
            : text(props.sendLabel) || 'Send'}
        </button>
      </div>
      {noted ? <OfflineNote value={props.offlineNote} color={d.muted} top={10} /> : null}
    </div>
  );
}

function deadlineOf(v) {
  if (v == null || v === '') return null;
  const t = typeof v === 'number' ? v : Date.parse(String(v));
  return Number.isFinite(t) ? t : null;
}

// setTimeout overflows past ~24.8 days.
const MAX_TIMER = 2 ** 31 - 1;

/** The DoneRow's lead: the ink check, an alert in the error red (`tone: failed`) or an undo arrow (`tone: cancelled`). */
function DoneCoin({ p, tone, dark }) {
  const coin = { width: 28, height: 28, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' };
  if (tone === 'failed') {
    return <span data-done-coin="failed" style={coin}>{inboxGlyph('alert', { size: 26, color: dark ? '#FF453A' : '#D70015', stroke: 2 })}</span>;
  }
  if (tone === 'cancelled') {
    return <span data-done-coin="cancelled" style={{ ...coin, background: p.pill }}>{inboxGlyph('undo', { size: 14, color: p.ink, stroke: 2 })}</span>;
  }
  return <span data-done-coin="done" style={{ ...coin, background: p.ink }}><Check size={14} color={p.onInk} /></span>;
}

export function DoneRow({ props = {}, ctx, emit }) {
  const p = inboxPalette(ctx);
  const deadline = deadlineOf(props.undoUntil);
  const [now, setNow] = useState(() => Date.now());
  const open = deadline != null && deadline > now;
  useEffect(() => {
    if (deadline == null) return undefined;
    const wait = deadline - Date.now();
    const t = setTimeout(() => setNow(Date.now()), Math.max(0, Math.min(wait + 50, MAX_TIMER)));
    return () => clearTimeout(t);
  }, [deadline]);
  const undoLabel = props.undoLabel === undefined ? 'Undo' : text(props.undoLabel);
  const showUndo = open && Boolean(undoLabel);
  const tappable = props.chevron === true && Boolean(emit);
  return (
    <div data-done-row="" role={tappable ? 'button' : undefined} tabIndex={tappable ? 0 : undefined}
      onClick={tappable ? () => emit('tap', {}) : undefined}
      style={{
        lineHeight: TYPE, display: 'flex', alignItems: 'center', gap: 11, background: p.well, borderRadius: 16, padding: 12,
        color: p.ink, boxSizing: 'border-box', width: '100%', minWidth: 0, cursor: tappable ? 'pointer' : 'default',
      }}>
      <DoneCoin p={p} tone={props.tone} dark={ctx?.appearance === 'dark'} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <span style={{ fontSize: 14.5, fontWeight: 500, ...oneLine }}>{text(props.title)}</span>
        {props.sub ? <span style={{ fontSize: 12.5, color: p.meta, marginTop: 2, ...clamp(2) }}>{text(props.sub)}</span> : null}
      </div>
      {showUndo ? (
        <button type="button" data-done-undo="" onClick={(e) => { e.stopPropagation(); emit?.('undo', {}); }} style={{
          ...resetButton, display: 'inline-flex', alignItems: 'center', gap: 4, height: 30, padding: '0 11px', borderRadius: 15,
          background: p.pill, color: p.ink, fontSize: 13, fontWeight: 600, flex: '0 0 auto', whiteSpace: 'nowrap',
        }}><UndoGlyph />{undoLabel}</button>
      ) : null}
      {!showUndo && props.chevron === true ? <Chevron color={p.chevron} /> : null}
    </div>
  );
}

function slotList(raw) {
  return (Array.isArray(raw) ? raw : []).filter((s) => s && typeof s === 'object' && s.id != null);
}

function initialPick(slots, selected) {
  if (selected != null && slots.some((s) => s.id === selected)) return selected;
  const best = slots.find((s) => s.best === true);
  if (best) return best.id;
  const free = slots.find((s) => s.state === 'free');
  return free ? free.id : null;
}

function slotPayload(s) {
  return { id: s.id, label: s.label ?? null, state: s.state ?? null, start: s.start ?? null, end: s.end ?? null, best: s.best === true };
}

export function SlotPicker({ props = {}, ctx, emit }) {
  const p = inboxPalette(ctx);
  const slots = slotList(props.slots);
  const idKey = slots.map((s) => String(s.id)).join('\u0001');
  const [picked, setPicked] = useState(() => initialPick(slots, props.selected));
  const emitRef = useRef(emit);
  emitRef.current = emit;
  const announced = useRef(null);

  useEffect(() => {
    const next = initialPick(slots, props.selected);
    setPicked(next);
    if (announced.current === idKey) return;
    announced.current = idKey;
    const s = slots.find((x) => x.id === next);
    if (s) emitRef.current?.('select', slotPayload(s));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-run on the slot id set, not on array identity
  }, [idKey, props.selected]);

  const choose = (s) => {
    setPicked(s.id);
    emit?.('select', slotPayload(s));
  };
  const bestLabel = props.bestLabel === undefined ? 'Best' : text(props.bestLabel);

  return (
    <div data-slot-picker="" role="radiogroup" style={{ lineHeight: TYPE, display: 'flex', flexDirection: 'column', minWidth: 0, color: p.ink }}>
      {slots.map((s) => {
        const on = s.id === picked;
        return (
          <button key={s.id} type="button" role="radio" aria-checked={on} onClick={() => choose(s)} style={{
            ...resetButton, background: p.slot, borderRadius: 14, padding: '11px 12px', display: 'flex', alignItems: 'center', gap: 10,
            marginTop: 6, border: `1px solid ${on ? p.ink : 'transparent'}`, boxSizing: 'border-box', width: '100%', textAlign: 'left', color: p.ink,
          }}>
            <span data-slot-radio={on ? 'on' : 'off'} style={{
              width: 20, height: 20, borderRadius: '50%', boxSizing: 'border-box', flex: '0 0 auto',
              border: `1.5px solid ${on ? p.ink : p.radio}`, background: on ? p.ink : 'transparent',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            }}>{on ? <Check size={12} color={p.onInk} /> : null}</span>
            <span style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <span style={{ fontSize: 14, fontWeight: 600, ...oneLine }}>{text(s.label)}</span>
              {s.sub ? <span style={{ fontSize: 12.5, color: p.slotSub, marginTop: 2, ...oneLine }}>{text(s.sub)}</span> : null}
            </span>
            {s.best === true && bestLabel ? <Tag label={bestLabel} ink palette={p} /> : null}
          </button>
        );
      })}
      {props.connectLabel ? (
        <button type="button" data-slot-connect="" onClick={() => emit?.('connect', {})} style={{
          ...resetButton, display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, background: p.slot, borderRadius: 14,
          padding: '11px 12px', fontSize: 14, fontWeight: 600, color: p.ink, width: '100%', boxSizing: 'border-box', textAlign: 'left',
        }}>
          <Glyph name="calendar" size={18} color={p.ink} />
          <span style={{ flex: 1, ...oneLine }}>{text(props.connectLabel)}</span>
          <Chevron color={p.chevron} />
        </button>
      ) : null}
    </div>
  );
}

export function AgentBubble({ props = {}, ctx, children }) {
  const p = inboxPalette(ctx);
  const kids = (Array.isArray(children) ? children : [children]).filter(Boolean);
  const body = text(props.text);
  return (
    <div data-agent-bubble="" style={{ lineHeight: TYPE, display: 'flex', gap: 8, alignItems: 'flex-start', minWidth: 0, color: p.ink }}>
      <AgentMark size={26} palette={p} />
      <div style={{ flex: 1, minWidth: 0, background: p.well, borderRadius: '6px 20px 20px 20px', padding: '13px 12px 12px' }}>
        {body ? (
          <div style={{ fontSize: 15, lineHeight: 1.45, padding: `0 2px ${kids.length ? 10 : 0}px`, whiteSpace: 'pre-line', overflowWrap: 'anywhere' }}>{body}</div>
        ) : null}
        {kids}
      </div>
    </div>
  );
}

const MAX_FACES = 3;

/** A 0..1 fraction (clamped), or null when the value is absent or not a number. */
function unitOrNull(raw) {
  if (raw == null || raw === '' || !Number.isFinite(Number(raw))) return null;
  return Math.max(0, Math.min(1, Number(raw)));
}

export function Greeting({ props = {}, ctx }) {
  const p = inboxPalette(ctx);
  const line = joined(props.line);
  const faces = (Array.isArray(props.faces) ? props.faces : []).filter((f) => f && typeof f === 'object').slice(0, MAX_FACES);
  const progress = unitOrNull(props.progress);
  return (
    <div data-greeting="" style={{ lineHeight: TYPE, padding: '8px 2px', color: p.ink, minWidth: 0 }}>
      <div style={{ fontFamily: SERIF_FONT, fontWeight: 400, fontSize: 30, lineHeight: 1.1, overflowWrap: 'anywhere' }}>{text(props.title)}</div>
      {line || faces.length ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14.5, color: p.line, marginTop: 8, minWidth: 0 }}>
          {faces.length ? (
            <span data-greeting-faces="" style={{ display: 'flex', paddingRight: 7, flex: '0 0 auto' }}>
              {faces.map((f, i) => (
                <span key={i} style={{ marginRight: -7, display: 'inline-flex' }}>
                  <Face src={f.avatar_url ?? f.avatar} initial={f.initial} size={22} palette={p} ring={p.page} />
                </span>
              ))}
            </span>
          ) : null}
          {line ? <span style={{ minWidth: 0 }}>{line}</span> : null}
        </div>
      ) : null}
      {progress != null ? (
        <div data-greeting-progress="" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progress * 100)}
          style={{ height: 4, borderRadius: 2, background: p.track, marginTop: 14, overflow: 'hidden' }}>
          <i style={{ display: 'block', height: 4, width: `${progress * 100}%`, background: p.ink, borderRadius: 2 }} />
        </div>
      ) : null}
    </div>
  );
}

function boolOrNull(v) {
  if (v === true || v === 'true') return true;
  if (v === false || v === 'false') return false;
  return null;
}

export function ToggleRow({ props = {}, ctx, emit }) {
  const p = inboxPalette(ctx);
  const seeded = boolOrNull(props.value);
  const [on, setOn] = useState(seeded);
  useEffect(() => { setOn(seeded); }, [seeded]);
  const flip = () => {
    if (on == null) return;
    const next = !on;
    setOn(next);
    emit?.('changed', { value: next });
  };
  const label = text(props.title);
  const knob = useMemo(() => (on ? p.onInk : '#FFFFFF'), [on, p]);
  return (
    <div data-toggle-row="" style={{
      lineHeight: TYPE, display: 'flex', alignItems: 'center', gap: 12, padding: '13px 2px', color: p.ink, minWidth: 0,
      borderBottom: props.divider === false ? 'none' : `1px solid ${p.hair}`,
    }}>
      {props.icon ? <Glyph name={String(props.icon)} size={18} color={p.ink} /> : null}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <span style={{ fontSize: 14, fontWeight: 500, overflowWrap: 'anywhere' }}>{label}</span>
        {props.sub ? <span style={{ fontSize: 12, color: p.meta, marginTop: 2, overflowWrap: 'anywhere' }}>{text(props.sub)}</span> : null}
      </div>
      {on == null ? null : (
        <button type="button" role="switch" aria-checked={on} aria-label={label} onClick={flip} style={{
          ...resetButton, width: 44, height: 26, borderRadius: 13, position: 'relative', flex: '0 0 auto',
          background: on ? p.ink : p.togOff, transition: 'background 160ms var(--ease)',
        }}>
          <i style={{
            position: 'absolute', top: 2, left: on ? 20 : 2, width: 22, height: 22, borderRadius: '50%',
            background: knob, boxShadow: '0 1px 3px rgba(0,0,0,.2)', transition: 'left 160ms var(--ease)',
          }} />
        </button>
      )}
    </div>
  );
}

/** A string prop that may also be a list of parts (literals and binds), concatenated; a missing part drops it. */
function joined(v) {
  if (Array.isArray(v)) {
    if (v.some((part) => part == null || text(part) === '')) return null;
    const out = v.map(text).join('');
    return out.trim() ? out : null;
  }
  return v == null || text(v).trim() === '' ? null : text(v);
}

const DOT = { good: '#34C759', warn: '#FF9F0A', gray: '#C7C7CC' };

export function InboxHeader({ props = {}, ctx, emit }) {
  const p = inboxPalette(ctx);
  const variant = props.variant || 'home';
  const title = text(props.title);
  const sub = joined(props.sub);
  const trailing = props.trailing ? String(props.trailing) : null;
  const host = useContext(PageHeaderHostContext) || {};
  const hostActions = (host.actions?.length ?? 0) > 0;
  const trailingBox = (size) => {
    const glyph = trailing ? (inboxGlyph(trailing, { size, color: p.ink }) || <Icon props={{ name: trailing, size, tone: p.ink }} />) : null;
    const box = { ...resetButton, width: 36, height: 36, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto', color: p.ink };
    return glyph
      ? <button type="button" data-inbox-header-trailing="" aria-label={trailing} onClick={() => emit?.('trailing', {})} style={box}>{glyph}</button>
      : <span aria-hidden style={box} />;
  };
  if (variant === 'home') {
    const dot = DOT[props.dot];
    return (
      <div data-inbox-header="home" style={{ lineHeight: TYPE, display: 'flex', alignItems: 'center', padding: `6px ${hostActions ? 108 : 16}px 10px ${host.leading ? 56 : 16}px`, color: p.ink, minWidth: 0 }}>
        {props.mark === false ? null : emit ? (
          <button type="button" data-inbox-header-mark="" aria-label="What I did today" onClick={() => emit('mark', {})}
            style={{ ...resetButton, marginRight: 10, display: 'inline-flex', cursor: 'pointer' }}>
            <AgentMark size={30} palette={p} />
          </button>
        ) : <span style={{ marginRight: 10, display: 'inline-flex' }}><AgentMark size={30} palette={p} /></span>}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <span style={{ fontSize: 17, fontWeight: 600, ...oneLine }}>{title}</span>
          {sub ? (
            <span style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 2, fontSize: 12, color: p.statLabel, minWidth: 0 }}>
              {dot ? <i data-inbox-header-dot="" style={{ width: 6, height: 6, borderRadius: '50%', background: dot, flex: '0 0 auto' }} /> : null}
              <span style={oneLine}>{sub}</span>
            </span>
          ) : null}
        </div>
        {hostActions ? null : trailingBox(18)}
      </div>
    );
  }
  const nav = variant === 'nav';
  const words = (
    <span style={{ display: 'flex', flexDirection: 'column', alignItems: nav ? 'flex-start' : 'center', minWidth: 0 }}>
      <b style={{ fontSize: nav ? 15 : 17, fontWeight: 600, ...oneLine, maxWidth: '100%' }}>{title}</b>
      {sub ? <span style={{ fontSize: 11.5, color: p.meta, ...oneLine, maxWidth: '100%' }}>{sub}</span> : null}
    </span>
  );
  return (
    <div data-inbox-header={variant} style={{
      lineHeight: TYPE, display: 'flex', alignItems: 'center', padding: '2px 10px 8px', color: p.ink, minWidth: 0,
      borderBottom: `1px solid ${ctx?.appearance === 'dark' ? '#1C1C1E' : p.hair}`,
    }}>
      <span aria-hidden style={{ width: 36, height: 36, flex: '0 0 auto' }} />
      <div style={{ flex: 1, display: 'flex', justifyContent: 'center', minWidth: 0 }}>
        {nav ? (
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            <Face src={props.avatar} initial={props.initial || title} size={26} palette={p} />
            {words}
          </span>
        ) : words}
      </div>
      {trailingBox(20)}
    </div>
  );
}

export function InboxRow({ props = {}, ctx, emit, slots }) {
  const p = inboxPalette(ctx);
  const spinning = submitSpin(slots);
  const tone = props.tone || 'plain';
  const alert = tone === 'alert';
  const dark = ctx?.appearance === 'dark';
  const alertInk = dark ? '#FFB340' : '#8A5300';
  const str = (v) => (v == null || v === '' ? null : text(v));
  const title = text(props.title);
  const sub = str(props.sub);
  const time = str(props.time);
  const icon = str(props.icon);
  const aside = str(props.aside);
  const tag = str(props.tag);
  const actionLabel = str(props.actionLabel);
  const outline = props.actionStyle === 'outline';
  const hasFace = str(props.avatar) != null || str(props.initial) != null;
  const progress = unitOrNull(props.progress);
  const glyph = (name, size, color) => inboxGlyph(name, { size, color }) || <Icon props={{ name, size, tone: color }} />;
  const circle = (size, child) => (
    <span style={{ width: size, height: size, borderRadius: '50%', background: p.tagBg, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>{child}</span>
  );

  let lead = null;
  if (time != null) {
    lead = (
      <span style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flex: '0 0 auto' }}>
        <span style={{ width: 36, paddingTop: 6, fontSize: 12, fontWeight: 500, color: p.meta, ...oneLine }}>{time}</span>
        {circle(26, glyph(icon || 'check', 13, p.ink))}
      </span>
    );
  } else if (hasFace) {
    lead = <Face src={str(props.avatar)} initial={str(props.initial) || title} size={32} palette={p} />;
  } else if (icon != null) {
    lead = tone === 'plain' || tone === 'list' ? circle(32, glyph(icon, 17, p.ink)) : glyph(icon, 18, alert ? alertInk : p.ink);
  }

  let trailing = null;
  if (actionLabel != null) {
    const rowId = ctx?.item?.id ?? null;
    const spin = spinning({ row: rowId, rowKey: 'id' });
    trailing = (
      <button type="button" data-inbox-row-action="" data-spinning={spin || undefined} disabled={spin || undefined}
        aria-busy={spin || undefined} onClick={(e) => {
          e.stopPropagation();
          if (!spin) emit?.('action', rowId != null ? { id: rowId } : {});
        }} style={{
        ...resetButton, height: outline ? 28 : 30, padding: '0 12px', borderRadius: 15, flex: '0 0 auto', boxSizing: 'border-box',
        background: outline ? p.card : p.ink, color: outline ? p.ink : p.onInk, border: outline ? `1px solid ${p.chipLine}` : 'none',
        fontSize: outline ? 12.5 : 13, fontWeight: 600, display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap',
      }}>{spin ? <SpinInPlace spin size={12} color={outline ? p.ink : p.onInk}>{actionLabel}</SpinInPlace> : actionLabel}</button>
    );
  } else if (aside != null || tag != null) {
    trailing = (
      <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flex: '0 0 auto' }}>
        {aside != null ? <span style={{ fontSize: 12, color: p.meta, whiteSpace: 'nowrap' }}>{aside}</span> : null}
        {tag != null ? <Tag label={tag} palette={p} /> : null}
      </span>
    );
  } else if (props.chevron === true) {
    trailing = <Chevron color={p.chevron} />;
  }

  const box = {
    list: { padding: '10px 2px', borderBottom: `1px solid ${p.hair}` },
    well: { padding: 12, background: p.well, borderRadius: 16 },
    alert: {
      padding: 12, borderRadius: 16, background: dark ? '#2B2111' : '#FFF8EC',
      border: `1px solid ${dark ? '#4D3A16' : '#F5D9A8'}`,
    },
  }[tone] || { padding: time != null ? '9px 0' : '6px 2px' };
  // `tappable: false` leaves the tap to a host collection cell (its itemActivated).
  const tap = emit && props.tappable !== false ? () => emit('tap', {}) : undefined;
  return (
    <div data-inbox-row={tone} role={tap ? 'button' : undefined} tabIndex={tap ? 0 : undefined} onClick={tap}
      style={{
        lineHeight: TYPE, display: 'flex', alignItems: time != null ? 'flex-start' : 'center', gap: time != null ? 10 : 11, color: p.ink,
        boxSizing: 'border-box', width: '100%', minWidth: 0, cursor: tap ? 'pointer' : 'default', ...box,
      }}>
      {lead}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <span style={{ fontSize: 14.5, fontWeight: tone === 'list' ? 600 : 500, ...clamp(2) }}>{title}</span>
        {sub != null ? <span style={{ fontSize: 12.5, color: alert ? alertInk : p.meta, marginTop: 2, ...clamp(2) }}>{sub}</span> : null}
        {progress != null ? (
          <span data-inbox-row-progress="" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progress * 100)}
            style={{ display: 'block', height: 3, borderRadius: 1.5, background: p.track, marginTop: 7, overflow: 'hidden' }}>
            <i style={{ display: 'block', height: 3, width: `${progress * 100}%`, background: p.ink, borderRadius: 1.5 }} />
          </span>
        ) : null}
      </div>
      {trailing}
    </div>
  );
}

const INK_TONE = { note: 'note', meta: 'meta', line: 'line', sub: 'statLabel' };

const SECTION_LABEL = { fontSize: 11, fontWeight: 600, letterSpacing: 1.1, textTransform: 'uppercase', lineHeight: TYPE, ...oneLine };

/** One run at an exact design size, weight and ink; `box` draws a well or a dashed note around it. */
export function InboxText({ props = {}, ctx, emit }) {
  const p = inboxPalette(ctx);
  const raw = joined(props.value) || '';
  const value = props.preserveNewlines === true ? raw : raw.replace(/\s+/g, ' ').trim();
  const color = p[INK_TONE[props.tone]] || p.ink;
  const weight = [500, 600, 700].includes(Number(props.weight)) ? Number(props.weight) : 400;
  const lines = Number(props.maxLines) > 0 ? Number(props.maxLines) : null;
  const run = {
    fontSize: Number(props.size) || 14, fontWeight: weight, lineHeight: Number(props.lineHeight) || TYPE, color,
    textAlign: props.align === 'center' || props.align === 'end' ? props.align : 'start',
    whiteSpace: props.preserveNewlines === true ? 'pre-line' : 'normal', overflowWrap: 'anywhere', minWidth: 0,
    textDecoration: props.underline === true ? 'underline' : 'none', textUnderlineOffset: 3,
    ...(lines ? clamp(lines) : null),
  };
  const box = {
    well: { background: p.well, borderRadius: 16, padding: 14 },
    note: { border: `1px dashed ${p.chipLine}`, borderRadius: 14, padding: 12 },
  }[props.box] || null;
  const tap = props.tappable === true && emit ? () => emit('tap', {}) : undefined;
  const eyebrow = joined(props.eyebrow);
  return (
    <div data-inbox-text={props.box || ''} role={tap ? 'button' : undefined} tabIndex={tap ? 0 : undefined} onClick={tap}
      style={{ boxSizing: 'border-box', width: box ? '100%' : undefined, cursor: tap ? 'pointer' : 'default', ...box }}>
      {eyebrow ? <div data-inbox-text-eyebrow="" style={{ ...SECTION_LABEL, color: p.meta, marginBottom: 6 }}>{eyebrow}</div> : null}
      <div style={run}>{value}</div>
    </div>
  );
}

const BUTTON_SIZE = { big: [50, 16], sm: [36, 13.5] };

/** The design's pill button: solid ink or hairline outline; `enabled: false` draws the board's dim fill. */
export function InboxButton({ props = {}, ctx, emit, nodeId, slots }) {
  const p = inboxPalette(ctx);
  const spinning = submitSpin(slots);
  const dark = ctx?.appearance === 'dark';
  const label = joined(props.label) || '';
  const enabled = props.enabled === undefined ? true : Boolean(props.enabled);
  const offline = enabled && props.offline === true;
  const [height, size] = BUTTON_SIZE[props.size] || [44, 15];
  let look;
  if (!enabled) look = { background: dark ? '#2C2C2E' : '#E3E3E8', color: dark ? '#636366' : p.meta };
  else if (props.look === 'outline') look = { background: p.page, color: p.ink, border: `1px solid ${p.chipLine}` };
  else look = { background: p.ink, color: p.onInk };
  const rowId = ctx?.item?.id ?? null;
  const spin = enabled && !offline && spinning({ row: rowId });
  const activated = { action: nodeId || null, label, ...(rowId != null ? { row_id: rowId } : {}) };
  return (
    <button type="button" data-inbox-button={props.look || 'solid'} disabled={!enabled || spin} aria-label={label}
      aria-disabled={offline || undefined} aria-busy={spin || undefined} data-spinning={spin || undefined}
      onClick={enabled && !spin ? () => (offline ? emit?.('offline', {}) : emit?.('activated', activated)) : undefined}
      style={{
        ...resetButton, boxSizing: 'border-box', height, borderRadius: height / 2, padding: '0 8px',
        width: props.block === true ? '100%' : undefined, display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: size, fontWeight: 600, letterSpacing: 0, lineHeight: TYPE, minWidth: 0,
        cursor: enabled && !offline ? 'pointer' : 'default', opacity: offline ? 0.38 : 1, ...look,
      }}>
      <SpinInPlace spin={spin} size={16} color={look.color}>
        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>{label}</span>
      </SpinInPlace>
    </button>
  );
}

/** `body`: the approve sheet's ink-bordered editor that grows with its text; `hint`: a sparkle line that submits. */
export function InboxField({ props = {}, ctx, emit }) {
  const p = inboxPalette(ctx);
  const seed = text(props.value);
  const [value, setValue] = useState(seed);
  useEffect(() => { setValue(seed); }, [seed]);
  const area = useRef(null);
  useEffect(() => {
    const el = area.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);
  const base = { ...resetButton, border: 'none', outline: 'none', background: 'transparent', width: '100%', letterSpacing: 0, color: p.ink, fontFamily: 'inherit' };
  if (props.variant === 'hint') {
    const submit = () => {
      const out = value.trim();
      if (!out) return;
      emit?.('submitted', { text: out });
      setValue('');
    };
    return (
      <div data-inbox-field="hint" style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
        <Glyph name="sparkle" size={14} color={p.meta} />
        <input value={value} placeholder={text(props.placeholder)} onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
          style={{ ...base, fontSize: 13, lineHeight: TYPE, flex: 1, minWidth: 0 }} />
      </div>
    );
  }
  return (
    <div data-inbox-field="body" style={{ boxSizing: 'border-box', width: '100%', padding: 14, borderRadius: 16, background: p.card, border: `1.5px solid ${p.ink}` }}>
      <textarea ref={area} rows={1} value={value} placeholder={text(props.placeholder)}
        onChange={(e) => { setValue(e.target.value); emit?.('changed', { value: e.target.value }); }}
        style={{ ...base, display: 'block', resize: 'none', overflow: 'hidden', fontSize: 15, lineHeight: 1.45, caretColor: '#0A84FF' }} />
    </div>
  );
}

/** The board's segmented pills (`All N` first); emits `changed {key}`, null for All. */
export function InboxFilter({ props = {}, ctx, emit }) {
  const p = inboxPalette(ctx);
  const [picked, setPicked] = useState(props.selected == null || props.selected === '' ? null : String(props.selected));
  const options = [
    ...(props.allLabel ? [[null, text(props.allLabel)]] : []),
    ...(Array.isArray(props.options) ? props.options : [])
      .filter((o) => o && o.key != null && o.key !== '')
      .map((o) => [String(o.key), text(o.label)]),
  ];
  const pick = (key) => { setPicked(key); emit?.('changed', { key }); };
  return (
    <div data-inbox-filter="" style={{ display: 'flex', gap: 6, overflowX: 'auto', scrollbarWidth: 'none', minWidth: 0 }}>
      {options.map(([key, label]) => {
        const on = key === picked;
        return (
          <button key={key ?? '__all'} type="button" aria-pressed={on} onClick={() => pick(key)} style={{
            ...resetButton, flex: '0 0 auto', boxSizing: 'border-box', height: 30, padding: '0 11px', borderRadius: 15,
            border: `1px solid ${on ? p.ink : p.chipLine}`, background: on ? p.ink : 'transparent', color: on ? p.onInk : p.ink,
            fontSize: 12.5, fontWeight: 500, letterSpacing: 0, lineHeight: TYPE, whiteSpace: 'nowrap', cursor: 'pointer',
          }}>{label}</button>
        );
      })}
    </div>
  );
}

function stepList(raw) {
  return (Array.isArray(raw) ? raw : []).filter((s) => s && typeof s === 'object' && text(s.label).trim());
}

/** The index of the step that is now: `current` when it names an open step, else the first open one. */
function currentStep(steps, raw) {
  const n = raw == null || raw === '' ? NaN : Number(raw);
  if (Number.isInteger(n) && n >= 0 && n < steps.length && steps[n].done !== true) return n;
  return steps.findIndex((s) => s.done !== true);
}

function StepRing({ state, p }) {
  const now = state === 'now';
  return (
    <span data-step-ring={state} aria-hidden style={{
      width: 18, height: 18, borderRadius: '50%', boxSizing: 'border-box', flex: '0 0 auto', marginTop: 1,
      border: state === 'done' ? 'none' : `1.5px solid ${now ? p.ink : p.radio}`, background: state === 'done' ? p.ink : 'transparent',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {now ? <i style={{ width: 8, height: 8, borderRadius: '50%', background: p.ink, display: 'block' }} /> : null}
      {state === 'done' ? <Check size={11} color={p.onInk} /> : null}
    </span>
  );
}

/** A task's steps: the done ones folded behind one row, then a label and the open ones, the current ring filled. */
export function InboxSteps({ props = {}, ctx, emit }) {
  const p = inboxPalette(ctx);
  const steps = stepList(props.steps);
  const [open, setOpen] = useState(props.open === true);
  const now = currentStep(steps, props.current);
  const done = steps.filter((s) => s.done === true);
  const ahead = steps.map((s, i) => [s, i]).filter(([s]) => s.done !== true);
  const doneLabel = joined(props.doneLabel) || `${done.length} ${done.length === 1 ? 'step' : 'steps'} done`;
  const nextLabel = props.nextLabel === undefined ? 'Next' : text(props.nextLabel);
  const toggle = () => {
    const next = !open;
    setOpen(next);
    emit?.('toggled', { open: next });
  };
  const row = (s, state, key) => (
    <div key={key} data-step={state} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '9px 2px', minWidth: 0 }}>
      <StepRing state={state} p={p} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <span style={{ fontSize: 14.5, fontWeight: state === 'now' ? 600 : 500, color: state === 'now' ? p.ink : p.meta, ...clamp(2) }}>{text(s.label)}</span>
        {s.sub ? <span style={{ fontSize: 12.5, color: state === 'now' ? p.meta : p.radio, marginTop: 2, ...clamp(2) }}>{text(s.sub)}</span> : null}
      </div>
    </div>
  );
  return (
    <div data-inbox-steps="" style={{ lineHeight: TYPE, display: 'flex', flexDirection: 'column', minWidth: 0, color: p.ink }}>
      {done.length ? (
        <button type="button" data-inbox-steps-done="" aria-expanded={open} onClick={toggle} style={{
          ...resetButton, display: 'flex', alignItems: 'center', gap: 8, padding: '10px 2px', width: '100%', boxSizing: 'border-box',
          textAlign: 'left', color: p.note, fontSize: 13.5, fontWeight: 500, cursor: 'pointer',
        }}>
          <span style={{ flex: 1, ...oneLine }}>{doneLabel}</span>
          <span style={{ display: 'inline-flex', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 160ms var(--ease)' }}>
            <Chevron color={p.chevron} />
          </span>
        </button>
      ) : null}
      {open ? done.map((s, i) => row(s, 'done', `d${i}`)) : null}
      {ahead.length && nextLabel ? (
        <div data-inbox-steps-label="" style={{ ...SECTION_LABEL, color: p.meta, marginTop: done.length ? 16 : 0, padding: '0 2px 4px' }}>{nextLabel}</div>
      ) : null}
      {ahead.map(([s, i]) => row(s, i === now ? 'now' : 'later', `a${i}`))}
    </div>
  );
}
